import React, { useState } from 'react';
import { ChevronDown, ChevronUp, CheckCircle2, XCircle } from 'lucide-react';
import { formatFeatureLabel, INTEGRATION_FEATURES } from '../../utils/companyFeatures.js';
import {
  WHATSAPP_EVENT_META,
  mergeWhatsappNotificationSettings,
  WHATSAPP_OPTIONS_KEY,
} from '../../utils/whatsappNotificationSettings.js';

const PANEL_META = {
  whatsapp_notification: {
    hint: 'Sender / receiver / invoice options per shipment event, ML sheet alerts, plus optional delivery OTP',
  },
  email_notification: {
    hint: 'Company SMTP for outbound emails',
  },
  payment_gateway: {
    hint: 'Razorpay UPI / Online QR credentials',
  },
  ewaybill: {
    hint: 'PeriOne API e-way bill (company username, password, GSTIN)',
  },
  gst_verification: {
    hint: 'PeriOne GST search on shipment GST fields',
  },
};

const CompanyIntegrationViewPanels = ({ company, canAccess }) => {
  const [openKey, setOpenKey] = useState(null);
  const whatsappSettings = mergeWhatsappNotificationSettings(
    canAccess?.[WHATSAPP_OPTIONS_KEY] || company?.can_access?.[WHATSAPP_OPTIONS_KEY]
  );

  const toggleOpen = (key) => {
    setOpenKey((prev) => (prev === key ? null : key));
  };

  const renderOpenContent = () => {
    if (!openKey) return null;

    if (openKey === 'whatsapp_notification') {
      if (!canAccess.whatsapp_notification) {
        return <p className="text-sm text-gray-500">WhatsApp Notification is disabled for this company.</p>;
      }
      return (
        <div className="space-y-3">
          {WHATSAPP_EVENT_META.map((event) => (
            <div key={event.key} className="border border-gray-200 rounded-xl p-4">
              <p className="text-sm font-semibold text-gray-800">{event.label}</p>
              <p className="text-xs text-gray-500 mb-2">{event.hint}</p>
              <div className="flex flex-wrap gap-2">
                {event.options.map((option) => {
                  if (option.type === 'text') {
                    const value = whatsappSettings?.[event.key]?.[option.key] || '';
                    return (
                      <span
                        key={option.key}
                        className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-medium border bg-gray-50 text-gray-700 border-gray-200"
                      >
                        {option.label}: {value || option.placeholder || 'Default'}
                      </span>
                    );
                  }
                  const enabled = Boolean(whatsappSettings?.[event.key]?.[option.key]);
                  return (
                    <span
                      key={option.key}
                      className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-medium border ${
                        enabled
                          ? 'bg-green-50 text-green-800 border-green-200'
                          : 'bg-gray-50 text-gray-500 border-gray-200'
                      }`}
                    >
                      {enabled ? (
                        <CheckCircle2 className="w-3.5 h-3.5" />
                      ) : (
                        <XCircle className="w-3.5 h-3.5" />
                      )}
                      {option.label}
                    </span>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      );
    }

    if (openKey === 'email_notification') {
      if (!canAccess.email_notification) {
        return <p className="text-sm text-gray-500">Email Notification is disabled for this company.</p>;
      }
      const em = company.email_notification_settings || {};
      return (
        <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-3 gap-2 sm:gap-6">
          {[
            ['Mail Server', em.mail_server || 'Not Provided'],
            ['Mail Port', em.mail_port ?? 'Not Provided'],
            ['Default Sender', em.mail_default_sender || 'Not Provided'],
            ['Mail Username', em.mail_username || 'Not Provided'],
            ['Mail Password', em.has_mail_password ? 'Configured' : 'Not Provided'],
            [
              'TLS / SSL',
              `TLS: ${em.mail_use_tls !== false ? 'On' : 'Off'} · SSL: ${em.mail_use_ssl ? 'On' : 'Off'}`,
            ],
          ].map(([label, value]) => (
            <div key={label} className="bg-gray-50 p-4 rounded-xl border border-gray-200">
              <p className="text-xs text-gray-500 mb-1">{label}</p>
              <p className="text-sm font-semibold text-gray-900">{value}</p>
            </div>
          ))}
        </div>
      );
    }

    if (openKey === 'payment_gateway') {
      if (!canAccess.payment_gateway) {
        return <p className="text-sm text-gray-500">Payment Gateway is disabled for this company.</p>;
      }
      const pg = company.payment_gateway || {};
      return (
        <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-3 gap-2 sm:gap-6">
          {[
            ['Key ID', pg.key_id || 'Not Provided'],
            ['Key Secret', pg.has_key_secret ? 'Configured' : 'Not Provided'],
            ['Webhook Secret', pg.has_webhook_secret ? 'Configured' : 'Not Provided'],
          ].map(([label, value]) => (
            <div key={label} className="bg-gray-50 p-4 rounded-xl border border-gray-200">
              <p className="text-xs text-gray-500 mb-1">{label}</p>
              <p className="text-sm font-semibold text-gray-900">{value}</p>
            </div>
          ))}
        </div>
      );
    }

    if (openKey === 'ewaybill') {
      if (!canAccess.ewaybill) {
        return (
          <p className="text-sm text-gray-500">
            E-Way Bill API is disabled — shipments use manual e-way bill numbers.
          </p>
        );
      }
      return (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2 sm:gap-6">
          {[
            ['GSTIN', company.gst_number || 'Not Provided'],
            ['Username', company.ewaybill_username || 'Not Provided'],
            ['Password', company.has_ewaybill_password ? 'Configured' : 'Not Provided'],
          ].map(([label, value]) => (
            <div key={label} className="bg-gray-50 p-4 rounded-xl border border-gray-200">
              <p className="text-xs text-gray-500 mb-1">{label}</p>
              <p className="text-sm font-semibold text-gray-900 break-all">{value}</p>
            </div>
          ))}
        </div>
      );
    }

    if (openKey === 'gst_verification') {
      if (!canAccess.gst_verification) {
        return (
          <p className="text-sm text-gray-500">
            GST Verification is disabled — shipment GST numbers are not checked against PeriOne.
          </p>
        );
      }
      return (
        <p className="text-sm text-gray-600">
          Consignor and consignee GST numbers are verified after a full GSTIN is entered on Add or Edit Shipment.
        </p>
      );
    }

    return null;
  };

  return (
    <div>
      <h3 className="text-base sm:text-lg font-semibold text-gray-700 mb-4 pb-2 border-b-2 border-gray-200">
        Integrations
      </h3>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-2 sm:gap-3">
        {INTEGRATION_FEATURES.map((feature) => {
          const enabled = Boolean(canAccess[feature]);
          const isOpen = openKey === feature;
          const meta = PANEL_META[feature] || {};
          return (
            <button
              key={feature}
              type="button"
              onClick={() => toggleOpen(feature)}
              className={`text-left border rounded-xl p-4 transition-all ${
                isOpen
                  ? 'border-orange-300 bg-orange-50'
                  : enabled
                    ? 'border-green-300 bg-green-50 hover:bg-green-100'
                    : 'border-gray-300 bg-gray-50 hover:bg-gray-100'
              }`}
            >
              <div className="flex items-start justify-between gap-2">
                <div className="flex items-start gap-2 min-w-0">
                  {enabled ? (
                    <CheckCircle2 className="w-5 h-5 text-green-600 shrink-0" />
                  ) : (
                    <XCircle className="w-5 h-5 text-gray-400 shrink-0" />
                  )}
                  <div>
                    <p className={`text-sm font-semibold ${enabled ? 'text-green-900' : 'text-gray-600'}`}>
                      {formatFeatureLabel(feature)}
                    </p>
                    <p className="text-xs text-gray-500 mt-0.5">{meta.hint}</p>
                    <p className={`text-xs mt-1 ${enabled ? 'text-green-700' : 'text-gray-500'}`}>
                      {enabled ? 'Enabled' : 'Disabled'}
                    </p>
                  </div>
                </div>
                <span className="text-gray-500 shrink-0">
                  {isOpen ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                </span>
              </div>
            </button>
          );
        })}
      </div>

      {openKey && (
        <div className="mt-3 border border-gray-200 rounded-xl p-4 bg-white">
          {renderOpenContent()}
        </div>
      )}
    </div>
  );
};

export default CompanyIntegrationViewPanels;
